export interface MarkupEditorPageDimensions {
  width_points: number
  height_points: number
}

export interface MarkupEditorRenderSettings {
  pageWidth: number
  devicePixelRatio: number
  zoomPercent: number
}

const MAX_CSS_PAGE_WIDTH = 1152
const MIN_CSS_PAGE_WIDTH = 120
const MAX_DEVICE_PIXEL_RATIO = 2
const MAX_RENDER_PIXELS = 12_000_000

export const getFittedPageWidth = (availableWidth: number) =>
  Math.max(MIN_CSS_PAGE_WIDTH, Math.min(MAX_CSS_PAGE_WIDTH, availableWidth))

/** Keeps the PDF canvas within a fixed physical-pixel budget as zoom changes. */
export function getMarkupEditorRenderSettings(
  fittedPageWidth: number,
  page: MarkupEditorPageDimensions,
  zoom: number,
  devicePixelRatio: number
): MarkupEditorRenderSettings {
  const aspectRatio = page.height_points / page.width_points
  const limitedDevicePixelRatio = Math.max(
    1,
    Math.min(MAX_DEVICE_PIXEL_RATIO, devicePixelRatio)
  )
  const requestedPageWidth = fittedPageWidth * zoom
  // Keep the requested CSS size whenever the budget can be met by lowering
  // raster density. Only clamp the page when even DPR 1 would exceed it.
  const maxPageWidthAtMinimumDpr = Math.max(
    1,
    Math.floor(Math.sqrt(MAX_RENDER_PIXELS / aspectRatio))
  )
  const pageWidth = Math.min(requestedPageWidth, maxPageWidthAtMinimumDpr)
  const pageWidthClampedAtMinimumDpr =
    requestedPageWidth > maxPageWidthAtMinimumDpr
  const availableDevicePixelRatio = Math.sqrt(
    MAX_RENDER_PIXELS / (pageWidth * pageWidth * aspectRatio)
  )
  const renderDevicePixelRatio = Math.max(
    1,
    Math.min(
      limitedDevicePixelRatio,
      pageWidthClampedAtMinimumDpr ? 1 : availableDevicePixelRatio
    )
  )

  return {
    pageWidth,
    devicePixelRatio: renderDevicePixelRatio,
    zoomPercent: Math.round((pageWidth / fittedPageWidth) * 100),
  }
}
